import {AfterContentChecked,Component,EventEmitter,Input,OnChanges,OnInit,Output,ViewChild} from "@angular/core";
import {Localisation} from "@domain/geolocalisation/localisation";
import {TypeGeolocalisation} from "@domain/geolocalisation/typeGeolocalisation";
import {NgForm,NgModel} from "@angular/forms";
import {GeolocalisationTool} from "@domain/geolocalisation/geolocalisationTool";
import {TranslateService} from "@ngx-translate/core";
import {LieuService} from "../../lieu.service";
import {TypePortee} from "@domain/workflow/workflow";
import {filter,finalize,first} from "rxjs/operators";
import {Adresse} from "@domain/profil/adresse";
import {TypeAdresse} from "@domain/geolocalisation/type-adresse";
import {LieuParam} from "@domain/common/lieu/lieu-param";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {LieuGeolocalisationService} from "../../lieu-geolocalisation.service";

@Component({
    selector: 'localisation',
    host: {'data-test-id': 'localisation'},
    templateUrl: './lieu-localisation.component.html',
})
export class LieuLocalisationComponent implements OnInit,AfterContentChecked,OnChanges {
    /** Déclaration des enums pour le template */
    readonly TypePortee = TypePortee;

    /** Event émis lors de la sélection d'une localisation */
    @Output() onLocalisationSelected = new EventEmitter<Localisation>();

    /** Paramétrage du lieu */
    @Input() lieuParam: LieuParam;

    /** Localisation saisie / géolocalisée */
    @Input() localisation: Localisation = {};

    /** Type de l'adresse saisie */
    @Input() typeAdresse: TypeAdresse;

    /** Adresse source */
    @Input() adresse: Adresse;

    /** Configuration de l'outil de géolocalisation */
    @Input() geolocalisationTool: GeolocalisationTool;

    /** Modèle pour l'autocomplete du pays */
    saisiePays: any;

    /** Modèle pour l'autocomplete de la ville */
    saisieVille: any;

    /** Paramètre de configuration indiquant si la saisie d'une adresse est autorisée */
    isSaisieAutorisee: boolean = false;

    /** Flag permettant de fermer directement la popup lors d'un clic sur le bouton valider de l'onglet de saisie */
    isAdresseModifiee: boolean = false;

    /** Etat de validité du formulaire de saisie d'une adresse */
    isFormSaisieInvalid: boolean = false;

    /** Indique si l'adresse saisie doit être  publique (idUser == 0) ou privée (idUser != 0) */
    isAdressePublique: boolean = false;

    /** Indique si l'utilisateur a déjà cliqué sur la visibilité */
    isVisibiliteDefinie = false;

    /** Flag permettant de savoir si un chargement (ajax) est en cours */
    isLoading: boolean = false;

    /** Formulaire de saisie d'une adresse */
    @ViewChild("saisieForm")
    saisieForm: NgForm;

    /** Autocomplete du pays */
    @ViewChild("autocompletePays")
    autocompletePays: NgModel;

    /** Affichage ou masquage du champ de ville hors référentiel */
    isCustomVille: boolean = false;

    constructor(private translateService: TranslateService,
                private lieuService: LieuService,
                private lieuGeolocService: LieuGeolocalisationService,
                private confirmService: ConfirmService) {
    }

    ngOnInit(): void {
        //Initialisation du pays pour afficher la valeur dans l'autocomplete
        if (this.localisation?.pays) {
            this.saisiePays = {
                libelle: this.localisation.pays
            };
        }

        //Initialisation de la ville pour afficher la valeur dans l'autocomplete
        if (this.localisation?.ville) {
            this.saisieVille = {
                libelle: this.localisation.ville
            };
        }

        //Vérification de l'autorisation de saisie d'une adresse depuis la configuration du type entité
        this.isSaisieAutorisee = this.lieuParam.isSaisieAutorisee();

        //L'adresse est publique si elle appartient à l'admin ou qu'elle n'a pas de rue
        if (this.localisation?.idUser == 0 || !this.localisation?.rue) {
            this.isAdressePublique = true;
        }
    }

    /**
     * Initialisation du form à partir d'une adresse
     *
     * @param adresse Adresse d'origine
     */
    initFromAdresse(adresse: Adresse) {
        //Copie des informations de l'adresse dans la localisation
        this.localisation = Object.assign({},adresse) as Localisation;

        //On conserve la référence vers l'adresse pour la mise à jour
        this.adresse = adresse;

        //Mise à jour du pays pour l'autocomplete
        this.saisiePays = {
            libelle: this.localisation.pays
        };

        //Mise à jour de la ville pour l'autocomplete
        this.saisieVille = {
            libelle: this.localisation.ville
        };
    }

    /**
     * Détection des modifications
     */
    ngOnChanges(): void {
        //Vérification de l'état de validité du formulaire de saisie
        this.isFormSaisieInvalid = this.saisieForm?.invalid;
    }

    /**
     * Post-chargement des composants
     */
    ngAfterContentChecked(): void {
        //Initialisation de l'état de validité du formulaire de saisie
        this.isFormSaisieInvalid = this.saisieForm?.invalid;
    }

    /**
     * Mise à jour de l'objet de saisie avec le pays sélectionné dans l'autocomplete
     *
     * @param geographie Objet Geographie sélectionné dans l'autocomplete
     */
    onPaysChange(geographie: any) {
        //Si le pays choisi est différent de la valeur initiale. Comparaison sur le code et non l'id, car c'est la seule donnée enregistrée sur le lieu
        if (this.localisation.codePays != geographie.pays.code3) {
            this.setAdresseModifiee();
        }

        //Mise à jour de l'objet
        this.localisation.idPays = geographie?.pays?.idPays;
        this.localisation.pays = geographie?.pays?.libelle;
        this.localisation.codePays = geographie?.pays?.code3;

        //Si un pays différent a été sélectionné
        if (this.saisieVille?.pays?.idPays != geographie?.pays?.idPays) {
            //Effacement de la ville
            this.saisieVille = undefined;
        }
    }

    /**
     * Mise à jour de l'objet de saisie avec la ville sélectionnée dans l'autocomplete
     *
     * @param geographie Objet Geographie sélectionné dans l'autocomplete
     */
    onVilleChange(geographie: any) {
        //Si la ville choisie est différente de la valeur initiale
        if (this.localisation.ville != geographie.ville) {
            this.setAdresseModifiee();
        }

        //Mise à jour de l'objet ville
        this.localisation.ville = geographie?.ville?.libelle;

        //Mise à jour du département
        this.localisation.departement = geographie?.ville?.departementAdmin?.libelle;

        //Mise à jour de l'objet pays
        this.localisation.idPays = geographie?.pays?.idPays;
        this.localisation.pays = geographie?.pays?.libelle;
        this.localisation.codePays = geographie?.pays?.code3;
        if (this.localisation?.pays) {
            this.saisiePays = {
                libelle: this.localisation.pays
            };
        }
    }

    /**
     * Vérification de l'activation de l'outil de géolocalisation
     */
    isGeoloc() {
        return this.geolocalisationTool && this.geolocalisationTool.geolocalisationTool != TypeGeolocalisation.DISABLED;
    }

    /**
     * Handler du bouton valider
     */
    onValider() {
        //Si on a le paramétrage correspondant et que  l'adresse est modifiée et n'est pas localisable
        if (this.lieuParam.isValiderWarningWithoutGeolocalisation && this.isAdresseModifiee && this.localisation.longitude == null) {
            //Affichage d'un bouton de confirmation pour valider
            this.confirmService.showConfirm(this.translateService.instant('lieu.adresseNonGeolocaliseeConfirmation'))
                .pipe(filter(isConfirmed => isConfirmed)).subscribe({
                next: () => {
                    this.validerSaisie(false);
                }
            });
        } else {
            this.validerSaisie(false);
        }
    }

    /**
     * Validation de la saisie d'une adresse.
     * Fait appel à la géolocalisation si active.
     *
     * @param tryGeoloc false pour un appel sans géolocalisation (true par défaut)
     */
    validerSaisie(tryGeoloc: boolean = true): void {
        //Vérification que le type entité autorise la saisie d'une adresse
        if (this.isSaisieAutorisee) {
            //Vérification de l'activation de la géolocalisation
            if (this.isGeoloc() && tryGeoloc) {
                this.lieuGeolocService.openGeolocalisationTool(
                    this.geolocalisationTool,
                    this.localisation,
                    this.adresse,
                    this.lieuParam
                ).pipe(filter(isGeolocOk => isGeolocOk !== null))
                 .subscribe({
                    next: data => {
                        //Vérification du résultat
                        if (data) {
                            this.onLocalisationSelected.emit(data.geolocalisation);
                        }
                    }
                });
            } else {
                const selectLocalisation = () => {
                    //Pas de géolocalisation => Création d'un lieu à partir des champs saisis
                    const geolocalisation = {
                        ...this.localisation,
                        adresse: this.lieuService.getAdresseFromLocalisation(this.localisation)
                    };

                    //Sélection de l'adresse saisie
                    this.onLocalisationSelected.emit(geolocalisation);
                }

                //Dans le cas de la modification d'une adresse du profil ou de l'admin, on l'enregistre avant de continuer
                if (this.lieuParam.isShowAdresseFiltre) {
                    //Indicateur d'enregistrement en cours
                    this.isLoading = true;

                    //Création
                    if (!this.localisation || !this.localisation.id) {
                        //Initialisation de l'objet
                        this.localisation = Object.assign({},this.localisation, {
                            listeVisibilite: this.lieuParam.listeVisibiliteLocalisation,
                            origine: this.lieuParam.origineLocalisation
                        });
                    }

                    //Adresse publique/privée
                    this.localisation.idUser = this.isAdressePublique ? 0 : this.lieuParam.idUser;

                    //Enregistrement
                    this.lieuService.saveAdresse(new Adresse(this.localisation)).pipe(first(),finalize(() => this.isLoading = false)).subscribe(res => {
                        //Mise à jour à partir du retour
                        this.localisation = {
                            id: res.geographie.id,
                            rue: res.geographie.entiteGeo?.libelle,
                            codePostal: res.geographie.entiteGeo?.codePostal,
                            ville: res.geographie.entiteGeo?.libelleVille,
                            pays: res.geographie.entiteGeo?.libellePays,
                            codePays: res.geographie.entiteGeo?.codePays2,
                            codePays3: res.geographie.entiteGeo?.codePays3
                        }

                        //Mise à jour de l'adresse d'origine à partir du retour
                        this.adresse = this.adresse ?? new Adresse();
                        this.adresse.rue = this.localisation.rue;
                        this.adresse.ville = this.localisation.ville;
                        this.adresse.codePostal = this.localisation.codePostal;
                        this.adresse.codePays = this.localisation.codePays3;
                        this.adresse.pays = this.localisation.pays;

                        //On continue avec l'adresse renseignée
                        selectLocalisation();
                    });
                } else {
                    //On continue avec l'adresse renseignée
                    selectLocalisation();
                }
            }
        } else {
            //Pas de modification, fermeture de la popup
            this.onLocalisationSelected.emit();
        }
    }

    /**
     * indique que l'adresse saisie a été modifiée
     */
    setAdresseModifiee(): void {
        this.isAdresseModifiee = true;

        //Si l'adresse n'a pas de rue, elle est forcément publique
        if (!this.localisation?.rue) {
            this.isAdressePublique = true;
        } else if (!this.isVisibiliteDefinie) {
            //Sinon, si elle n'a pas été forcée, elle est privée
            this.isAdressePublique = false;
        }
    }

    /**
     * Indique si le bouton Valider est désactivé
     */
    isValiderDisabled() {
        //Si on a la ville, le pays et le code postal selon l'affichage
        return (!this.isCustomVille
                && ((this.saisieVille == null || this.saisieVille == "")
                    || (this.lieuParam.isRueRequired && (this.localisation.rue == null || this.localisation.rue == ""))))
            || (this.isCustomVille
                && (this.saisiePays == null || this.saisiePays == "" || this.localisation.ville == null || this.localisation.ville == ""
                    || this.localisation.codePostal == null || this.localisation.codePostal == ""));
    }

    /**
     * Indique si le bouton Géolocaliser est désactivé
     */
    isGeolocaliserDisabled() {
        //Si on a la ville et le pays selon l'affichage
        return (!this.isCustomVille && (this.saisieVille == null || this.saisieVille == ""))
            || (this.isCustomVille
                && (this.saisiePays == null || this.saisiePays == "" || this.localisation.ville == null || this.localisation.ville == ""));
    }
}
